import React, { useEffect, useState } from 'react'
import useFetchData from '../../../hooks/UseFetchData'
import CustomTable from '../../../shared/CustomTable'
import { ClaimListColumns } from '../../../constants/BillingConstants'
import CustomPagination from '../../../shared/CustomPagination'
import { IoIosSend } from 'react-icons/io'
import { FaRedo } from 'react-icons/fa'
import { sendClaim, matchClaimInsurancePlan } from '../../../services/InvoicesService'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { EyeIcon } from '../../../utils/TableActionIcons'
import ClaimListFilters from '../../../pages/HIS/ClaimListFilters'
import ConfirmationModal from '../../../shared/ConfirmationModal'

function ClaimListTable ({ setPayload, payload, showType = 'default' }) {
  const { data, loading, executeFetch } = useFetchData()
  const [sendLoading, setSenLoading] = useState(false)
  const [showSendConfirmModal, setShowSendConfirmModal] = useState(false)
  const [selectedClaim, setSelectedClaim] = useState(null)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const fetchList = React.useCallback(
    async (payloadData = payload) => {
      try {
        await executeFetch('POST', `v3/invoices/listClaims`, payloadData)
      } catch (err) {
        console.log(err)
      }
    },
    [executeFetch, payload]
  )

  useEffect(() => {
    fetchList()
  }, [payload?.page, payload?.perPage])

  const onPageChange = async (newPage, newRowsPerPage) => {
    console.log(newRowsPerPage)
    let payloadDt = {
      ...payload,
      page: newPage,
      perPage: newRowsPerPage
    }
    setPayload(payloadDt)
  }

  const handleSendClaim = async () => {
    const claimId = selectedClaim?.claim_id
    if (!claimId || sendLoading) return

    setSenLoading(true)
    try {
      // Resolve/refresh the RCM insurance plan first; only submit if it succeeds.
      try {
        await matchClaimInsurancePlan(claimId)
      } catch (matchError) {
        toast.error(
          matchError?.response?.data?.error ||
            t('Failed to match the claim insurance plan')
        )
        return
      }

      const res = await sendClaim(claimId)
      toast.success(res?.message || t('Claim submitted successfully'))
      await fetchList()
    } catch (error) {
      if (error?.response) {
        toast.error(error?.response?.data?.error || t('Failed to send claim'))
      } else {
        toast.error(t('Submission status unknown. Refreshing claim status...'))
        await fetchList()
      }
    } finally {
      setSenLoading(false)
      setShowSendConfirmModal(false)
      setSelectedClaim(null)
    }
  }

  const handleSendClaimClick = (claim) => {
    setSelectedClaim(claim)
    setShowSendConfirmModal(true)
  }

  const action = {
    header: t('Actions'),
    body: rowData => {
      const claim = rowData?.claim
      const hasClaimId = Boolean(claim?.claim_id)
      const iconClass = sendLoading
        ? 'opacity-50 cursor-not-allowed'
        : 'cursor-pointer'
      return (
        <div className='flex gap-2 items-center'>
          <EyeIcon
            onClick={() =>
              navigate('/claim-details', { state: { claimDetails: rowData } })
            }
          />
          {hasClaimId &&
            (claim?.status === 'submitted' ? (
              <FaRedo
                className={`text-orange-500 ${iconClass}`}
                size={15}
                title={t('Resubmit Claim')}
                onClick={() => !sendLoading && handleSendClaimClick(claim)}
              />
            ) : (
              <IoIosSend
                className={`text-primary ${iconClass}`}
                size={18}
                title={t('Send Claim')}
                onClick={() => !sendLoading && handleSendClaimClick(claim)}
              />
            ))}
        </div>
      )
    }
  }

  const columns = [action, ...ClaimListColumns(t, showType)]
  const isResubmission =
    Number(selectedClaim?.submit_attempt_count) > 0 ||
    selectedClaim?.status === 'submitted'
  return (
    <div className='mt-2'>
      <div className='my-2'>
        <ClaimListFilters
          payloadData={payload}
          setPayloadData={setPayload}
          loading={loading}
          fetchNewData={fetchList}
          showBulkSubmit={showType !== 'submitted'}
        />
      </div>
      <CustomTable
        data={data?.data}
        isLoading={loading}
        columns={columns}
      />
      <CustomPagination
        currentPage={data?.page}
        rowsPerPage={data?.perPage}
        totalRecords={data?.totalCount}
        totalPages={
          data?.totalCount && data?.perPage
            ? Math.ceil(data?.totalCount / data?.perPage)
            : 1
        }
        onPageChange={onPageChange}
      />

      <ConfirmationModal
        visible={showSendConfirmModal}
        setVisible={setShowSendConfirmModal}
        header={isResubmission ? 'Resubmit Claim' : 'Send Claim'}
        message={
          isResubmission
            ? 'Are you sure you want to resubmit this claim? A new submission attempt will be recorded.'
            : 'Are you sure you want to send this claim? This action will submit the claim for processing.'
        }
        acceptLabel={isResubmission ? 'Resubmit' : 'Send'}
        rejectLabel='Cancel'
        onAccept={handleSendClaim}
        onReject={() => {
          setShowSendConfirmModal(false)
          setSelectedClaim(null)
        }}
        isLoading={sendLoading}
      />
    </div>
  )
}

export default ClaimListTable
